import { defineComponent as _defineComponent } from 'vue'
import { BuyBoxOffers, useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { COUNTRIES_BY_TRAFFIC, COUNTRY_EXCEPTIONS, WEB_LOCALES } from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';
import BuyboxInternationnalOffersRow from '@/features/international-offers/components/BuyboxInternationnalOffersRow.vue';
import LanguageDropDown from '@/features/international-offers/components/LanguageDropDown.vue';
import OffersDropdown from '@/features/international-offers/components/OffersDropdown.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { OfferLocation } from '@/helpers/offer-helper';
import Provider from '@/interfaces/provider';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { computed, provide, ref } from 'vue';
import { useInternationalOffersTracking } from '../tracking';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyboxInternationalOffers',
  props: {
    titleId: null,
    title: null,
    buyboxOffers: null,
    buyboxFilterType: null,
    isEpisodeBuybox: { type: Boolean }
  },
  emits: ["filterClicked", "onNotifyMe"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'filterClicked',
		{
			value,
			nonInteraction,
		}: {
			value: BuyBoxFilterOptions;
			nonInteraction?: boolean;
		}
	): void;

	(e: 'onNotifyMe'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	titleId: string;
	title: TitleDetail;
	buyboxOffers: BuyBoxOffers;
	buyboxFilterType: BuyBoxFilterOptions;
	isEpisodeBuybox?: boolean;
};

const NoOffers = () => import('@/features/buybox/components/list/NoOffers.vue');
const PromotionPlacement = () => import('@/features/buybox/components/list/PromotionPlacement.vue');





const { country, language } = useLanguageStore();
const { t } = useI18n();
const { trackInternationalOffers } = useInternationalOffersTracking({ title: () => props.title });

const searchPackages = ref<Provider[]>([]);
const searchLanguage = ref<string[]>([]);
const expand = ref(false);
const countriesExpanded = ref(false);

provide('searchPackages', searchPackages);
provide('searchLanguage', searchLanguage);
provide('setSearchPackages', setSearchPackages);
provide('offerLocation', OfferLocation.REGULAR_BUYBOX);
provide('setSearchLanguage', setSearchLanguage);

function setSearchLanguage(languages: string[]) {
	searchLanguage.value = languages;
}

function setSearchPackages(providerShortNames: Provider[]) {
	searchPackages.value = providerShortNames;
}

const { filteredRegularOffers, allRegularOffers } = useBuyBoxData({
	title: () => props.title,
	buyboxData: () => props.buyboxOffers as BuyBoxOffers,
	buyboxFilter: () => props.buyboxFilterType,
});

const totalPackages = computed(() => {
	const integerFormat = new Intl.NumberFormat(language.value);
	return integerFormat.format(props.title.totalPackages);
});

const offersLanguages = computed(() => {
	const languages = allRegularOffers.value.map(offer => [...offer.subtitleLanguages, ...offer.audioLanguages]).flat();
	return [...new Set(languages)];
});
provide('offersLanguages', offersLanguages);

const filteredProviders = computed(() => {
	const groupedPackages = allRegularOffers.value.reduce((acc, offer) => {
		if (!acc[offer.package.shortName]) {
			acc[offer.package.shortName] = [offer.package];
		}
		return acc;
	}, {} as Record<string, TitleOfferFragment['package'][]>);

	return Object.values(groupedPackages).flat();
});

provide('filteredProviders', filteredProviders);

const groupedOffersByCountry = computed(() => {
	const _filteredOffers = filteredRegularOffers.value
		.filter(
			offer =>
				searchPackages.value.length === 0 ||
				searchPackages.value.some(item => item.shortName === offer.package.shortName)
		)
		.filter(
			offer =>
				searchLanguage.value === null ||
				(searchLanguage.value.length > 0
					? offer.audioLanguages.some(lang => searchLanguage.value.includes(lang))
					: true) ||
				(searchLanguage.value.length > 0
					? offer.subtitleLanguages.some(lang => searchLanguage.value.includes(lang))
					: true)
		);
	const groupedOffers = [..._filteredOffers].reduce((acc, offer) => {
		if (!acc[offer.country]) {
			acc[offer.country] = [];
		}
		acc[offer.country].push(offer);
		return acc;
	}, {} as Record<string, TitleOfferFragment[]>);

	const groupedOffersByCountry = Object.entries(groupedOffers).map(([country, offers]) => ({
		country,
		offers,
	}));

	// note to this interim ordering solution and why we don't fully rely on BE for ordering:
	// we can't fully rely on the backend order, because we're concatenating different monetization types together (buy, rent, stream, etc).
	// even though a title would be ordered last within the buy monetization type group, after concatenation it would be higher up than the stream items.
	const orderedGroupedOffersByCountry = groupedOffersByCountry.sort((a, b) => {
		const webLocaleA = COUNTRY_EXCEPTIONS[a.country.toLowerCase()]
			? COUNTRY_EXCEPTIONS[a.country.toLowerCase()]
			: (a.country.toLowerCase() as WebLocale);
		const webLocaleB = COUNTRY_EXCEPTIONS[b.country.toLowerCase()]
			? COUNTRY_EXCEPTIONS[b.country.toLowerCase()]
			: (b.country.toLowerCase() as WebLocale);

		// order by (country) language:
		const languageScoreA = WEB_LOCALES[webLocaleA].split('_')[0] === language.value ? 1000 : 0;
		const languageScoreB = WEB_LOCALES[webLocaleB].split('_')[0] === language.value ? 1000 : 0;
		const orderScoreLanguage = languageScoreB - languageScoreA;

		// order by country traffic size:
		// secondly, order by the country traffic size.
		// the COUNTRIES_BY_TRAFFIC is ordered by highest to lowest traffic country
		const countryScoreA = COUNTRIES_BY_TRAFFIC.includes(webLocaleA)
			? COUNTRIES_BY_TRAFFIC.indexOf(webLocaleA) * -1
			: -100 + webLocaleB.localeCompare(webLocaleA);
		const countryScoreB = COUNTRIES_BY_TRAFFIC.includes(webLocaleB)
			? COUNTRIES_BY_TRAFFIC.indexOf(webLocaleB) * -1
			: -100 + webLocaleA.localeCompare(webLocaleB);
		const orderScoreCountry = countryScoreB - countryScoreA;

		return orderScoreLanguage + orderScoreCountry;
	});

	return orderedGroupedOffersByCountry;
});

const slicedGroupedOffersByCountry = computed(() => {
	return expand.value ? groupedOffersByCountry.value : groupedOffersByCountry.value.slice(0, 2);
});

function getCountryName(countryCode: string, lang: string) {
	// these countries have a "the" in front of their country in english, e.g. "Let us notify you when you can watch it in the United States".
	const countriesWithThe = ['US', 'GB', 'NL', 'PH', 'BS', 'MV', 'SC', 'KM', 'MH', 'SB', 'FM', 'CZ', 'CF', 'DO', 'AE'];
	if (countriesWithThe.includes(countryCode) && lang === 'en') {
		return 'the ' + t(`WEBAPP_COUNTRY_${countryCode.toUpperCase()}`);
	}
	return t(`WEBAPP_COUNTRY_${countryCode.toUpperCase()}`);
}

const countryName = computed(() => getCountryName(country.value, language.value));

// function handleFilterClick(payload: { value: BuyBoxFilterOptions; nonInteraction?: boolean }) {
// 	setNewBuyboxFilterType(payload.value);

// 	TrackingHelper.trackEvent('userinteraction', {
// 		action: 'filter_toggled',
// 		label: 'buybox',
// 		property: payload.value,
// 		nonInteraction: payload.nonInteraction,
// 	});
// }

function showMore() {
	expand.value = true;

	trackInternationalOffers('showMoreClicked', { spec: { property: '' } });
}

function showAllOffers() {
	countriesExpanded.value = true;

	trackInternationalOffers('showAllOffersWithinCountry');
}

return { __sfc: true,NoOffers, PromotionPlacement, props, emit, country, language, t, trackInternationalOffers, searchPackages, searchLanguage, expand, countriesExpanded, setSearchLanguage, setSearchPackages, filteredRegularOffers, allRegularOffers, totalPackages, offersLanguages, filteredProviders, groupedOffersByCountry, slicedGroupedOffersByCountry, getCountryName, countryName, showMore, showAllOffers, BuyboxInternationnalOffersRow, LanguageDropDown, OffersDropdown, faChevronDown }
}

})